/**
 * Environment detection for the Activity. Inside Discord the app is served from
 * `<APPLICATION_ID>.discordsays.com`; in a normal browser it is not.
 * @see docs/01-activity-build-guide.md §7  @see docs/02-tos-policy-and-builder-lessons.md
 */

/** True when running inside the Discord client (served through the proxy). */
export function isInDiscord(): boolean {
  return typeof location !== 'undefined' && location.hostname.includes('discordsays.com');
}

/**
 * Whether to use the Mock SDK. CRITICAL: the mock may NEVER run in production.
 * It is gated to dev builds (`import.meta.env.DEV`) AND only outside Discord.
 * A production build (even opened directly outside Discord) will use the real SDK.
 */
export function shouldUseMock(): boolean {
  return !isInDiscord() && import.meta.env.DEV === true;
}

/**
 * In Discord every request is forced through the proxy: prefix API calls with `/.proxy`.
 * Locally, Vite proxies `/api` to the Express server, so no prefix is needed.
 */
export function apiBase(): string {
  return isInDiscord() ? '/.proxy' : '';
}

/**
 * Launch params arrive on the iframe URL (e.g. `?restaurant=...&neighborhood=...`).
 * NEVER strip `location.search` on navigation — the SDK reads `frame_id` from it.
 */
export function launchParams(): URLSearchParams {
  return new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
}

/** The public Discord Application ID (safe to expose). */
export function discordClientId(): string {
  return import.meta.env.VITE_DISCORD_CLIENT_ID ?? '';
}
